import {
  UsersAddSubscriptionsComponent,
  IAssignedSubscriptionResult,
  UsersSubscriptionDialogMode,
} from '../users-add-subscriptions/users-add-subscriptions.component';
import { LoginAsParamService } from '../../../../api/api-services/login-as-param.service';
import { IPlatformUserAccountVm } from '../../../../api/platform-user-account-vm.model';
import { ILogiAsResultVm } from '../../../../api/logi-as-result-vm.model';
import { ILoginAsIm } from '../../../../api/login-as-im.model';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Subject, Subscription, catchError, map, of, switchMap } from 'rxjs';
import { PlatformUserDevicesService } from '../../../../api/api-services/platform-user-devices.service';
import { PlatformUserPasswordService } from '../../../../api/api-services/platform-user-password.service';
import { PlatformUserEmailConfirmationService } from '../../../../api/api-services/platform-user-email-confirmation.service';
import { PlatformUserActivationLinkService } from '../../../../api/api-services/platform-user-activation-link.service';
import { PlatformUserOskService } from '../../../../api/api-services/platform-user-osk.service';
import { OsksService } from '../../../../api/api-services/osks.service';
import { IPlatformUserDeviceVm } from '../../../../api/platform-user-device-vm.model';
import { IPlatformUserOskVm } from '../../../../api/platform-user-osk-vm.model';
import { IOskVm } from '../../../../api/osk-vm.model';
import { confirmAndResendActivationLink } from '../activation-link';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { eModalRoles } from '../../../shared/models/modals';

const DEVICES_TAB_INDEX = 2;

interface ReadOnlyData {
  name: Exclude<keyof IPlatformUserAccountVm, 'emailConfirmed'>;
  label: string;
  type:
    | 'input-string'
    | 'calendar'
  fieldWidth?: string;
  labelWidth?: string;
  fieldTooltip?: string;
}



@Component({
  selector: 'app-users-details',
  templateUrl: './users-details.component.html',
  styleUrl: './users-details.component.scss',
})
export class UsersDetailsComponent implements OnInit, OnDestroy {
  private _loginAsParamService = inject(LoginAsParamService);
  private _config = inject(DynamicDialogConfig);
  private _dynamicDialogRef = inject(DynamicDialogRef);
  private _messageService = inject(MessageService);
  private _primengDialogService = inject(DialogService);
  private _platformUserDevicesService = inject(PlatformUserDevicesService);
  private _platformUserPasswordService = inject(PlatformUserPasswordService);
  private _platformUserEmailConfirmationService = inject(PlatformUserEmailConfirmationService);
  private _platformUserActivationLinkService = inject(PlatformUserActivationLinkService);
  private _confirmationService = inject(ConfirmationService);
  private _platformUserOskService = inject(PlatformUserOskService);
  private _osksService = inject(OsksService);
  private _devicesRequest?: Subscription;
  private _oskRequest?: Subscription;
  private _oskSearch = new Subject<string>();
  private _accountActions = new Subscription();

  private assignSubscriptionRef: DynamicDialogRef | undefined;

  public data: {
    currentUserIndex: number;
    usersListPerPage: IPlatformUserAccountVm[];
    editMode: boolean;
    initialTab?: 'devices';
  } = this._config.data;

  public usedDetailsViewFields: ReadOnlyData[] = this.setReadOnlyFields();

  public currentUser: IPlatformUserAccountVm =
    this.data.usersListPerPage[this.data.currentUserIndex];
  public currentUserIndex: number = this.data.currentUserIndex;
  public isEditMode: boolean = false;

  public selectedTabIndex: number = 0;
  public permisions: string[] = [];
  public readonly devicesTabIndex = DEVICES_TAB_INDEX;

  /** App installations the user signed in on, most recently used first. */
  public devices: IPlatformUserDeviceVm[] = [];
  public devicesLoading = false;
  public devicesError: string | null = null;

  /** Guids of the accounts with a password reset, e-mail confirmation or activation link request in flight. */
  public passwordResetPending = new Set<string>();
  public emailConfirmationPending = new Set<string>();
  public activationLinkPending = new Set<string>();

  /** The OSK the user belongs to as a student; null until loaded. */
  public userOsk: IPlatformUserOskVm | null = null;
  public userOskLoading = false;
  public userOskError: string | null = null;
  public oskEditing = false;
  public oskSaving = false;
  /** Model of the OSK search box: the typed text, or the OSK picked from the suggestions. */
  public oskSelection: IOskVm | string | null = null;
  public oskSuggestions: IOskVm[] = [];

  public transColumns = [
    {
      field: 'creationDate',
      header: 'Data transakcji',
      sortable: true,
      columnClass: 'flex justify-content-between align-items-center',
    },
    {
      field: 'subscriptionType',
      header: 'Wartość',
      sortable: true,
      columnClass: '',
    },
    {
      field: 'subscriptionType',
      header: 'Faktura',
      columnClass: '',
    },
    {
      field: 'subscriptionType',
      header: 'Data wystawienia',
      columnClass: '',
    },

    {
      field: 'actions',
      header: 'Akcje',
      columnClass: 'flex justify-content-center align-items-center',
      actions: [
        {
          label: 'Wystaw fakturę',

          buttonClass: 'primary-button',
        },
      ],
    },
  ];

  ngOnInit(): void {
    this.currentUser = this.data.usersListPerPage[this.data.currentUserIndex];
    if (this.data.initialTab === 'devices') {
      this.selectedTabIndex = DEVICES_TAB_INDEX;
    }
    // The autocomplete debounces typing itself; switchMap drops the answer to an outdated query.
    this._accountActions.add(this._oskSearch.pipe(
      switchMap(query => this._osksService.get(query.trim(), 'oskName', 1, 20).pipe(
        map(result => result.data ?? []),
        catchError(() => of([] as IOskVm[]))))
    ).subscribe(osks => this.oskSuggestions = osks));
    this.loadDevices();
    this.loadUserOsk();
  }

  ngOnDestroy(): void {
    this._devicesRequest?.unsubscribe();
    this._oskRequest?.unsubscribe();
    this._accountActions.unsubscribe();
  }

  loadDevices(): void {
    this._devicesRequest?.unsubscribe();
    this.devices = [];
    this.devicesError = null;
    if (!this.currentUser?.guid) {
      return;
    }
    this.devicesLoading = true;
    this._devicesRequest = this._platformUserDevicesService.get(this.currentUser.guid).subscribe({
      next: devices => {
        this.devices = devices;
        this.devicesLoading = false;
      },
      error: () => {
        this.devicesError = 'Nie udało się pobrać urządzeń.';
        this.devicesLoading = false;
      },
    });
  }

  loadUserOsk(): void {
    this._oskRequest?.unsubscribe();
    this.userOsk = null;
    this.userOskError = null;
    this.cancelOskEdit();
    if (!this.currentUser?.guid) {
      return;
    }
    this.userOskLoading = true;
    this._oskRequest = this._platformUserOskService.get(this.currentUser.guid).subscribe({
      next: userOsk => {
        this.userOsk = userOsk;
        this.userOskLoading = false;
      },
      error: () => {
        this.userOskError = 'Nie udało się pobrać OSK użytkownika.';
        this.userOskLoading = false;
      },
    });
  }

  /** Short code and city of the user's OSK, e.g. "jeden, Wrocław". */
  get userOskDetails(): string {
    return [this.userOsk?.oskShortCode, this.userOsk?.oskCity].filter(Boolean).join(', ');
  }

  /** The OSK picked in the search box, or null while the box holds typed text only. */
  get selectedOsk(): IOskVm | null {
    return this.oskSelection && typeof this.oskSelection === 'object' ? this.oskSelection : null;
  }

  searchOsks(event: AutoCompleteCompleteEvent): void {
    this._oskSearch.next(event.query ?? '');
  }

  startOskEdit(): void {
    this.oskSelection = null;
    this.oskSuggestions = [];
    this.oskEditing = true;
  }

  cancelOskEdit(): void {
    this.oskEditing = false;
    this.oskSelection = null;
  }

  assignOsk(): void {
    const osk = this.selectedOsk;
    if (!osk) {
      return;
    }
    const previous = this.userOsk?.oskCompanyGuid ? this.userOsk.oskName : null;
    this.confirmOskChange('Przypisanie do OSK',
      `Przypisać ${this.currentUser.email} do OSK „${osk.oskName}”?` +
      (previous ? ` Użytkownik zniknie z listy kursantów „${previous}” i z jego bieżących grup wykładowych.` : '') +
      ' Abonament użytkownika się nie zmienia.',
      'Przypisz', osk.guid);
  }

  detachOsk(): void {
    this.confirmOskChange('Odpięcie od OSK',
      `Odpiąć ${this.currentUser.email} od OSK „${this.userOsk?.oskName}”? Użytkownik zniknie z listy kursantów ` +
      'tego OSK i z jego bieżących grup wykładowych. Abonament użytkownika się nie zmienia.',
      'Odepnij', null);
  }

  private confirmOskChange(header: string, message: string, acceptLabel: string, oskCompanyGuid: string | null): void {
    const user = this.currentUser;
    this._confirmationService.confirm({
      key: 'platformUserAccountAction',
      header,
      message,
      acceptLabel,
      rejectLabel: 'Anuluj',
      defaultFocus: 'reject',
      accept: () => {
        this.oskSaving = true;
        this._accountActions.add(this._platformUserOskService.put({ userGuid: user.guid, oskCompanyGuid }).subscribe({
          next: userOsk => {
            this.oskSaving = false;
            // The user is a row of the list behind the dialog, so its OSK column follows the change.
            user.oskCompanyGuid = userOsk.oskCompanyGuid;
            user.oskName = userOsk.oskName;
            if (this.currentUser.guid === user.guid) {
              this.userOsk = userOsk;
              this.cancelOskEdit();
            }
            this._messageService.add({ severity: 'success',
              summary: userOsk.oskCompanyGuid ? 'Przypisano do OSK' : 'Odpięto od OSK',
              detail: userOsk.oskCompanyGuid ? `${user.email} → ${userOsk.oskName}` : user.email });
          },
          error: error => {
            this.oskSaving = false;
            this._messageService.add({ severity: 'error', summary: 'Nie udało się zmienić OSK',
              detail: error?.error?.message });
          },
        }));
      },
    });
  }

  get currentUserSubscriptionRows(): IPlatformUserAccountVm[] {
    return this.currentUser?.subscriptionPlan ? [this.currentUser] : [];
  }

  get subscriptionState(): { label: string; color: string } {
    return this.getSubscriptionState(this.currentUser?.subscriptionPlanActiveTo);
  }

  closeDialog(status: eModalRoles = eModalRoles.CLOSE): void {
    this._dynamicDialogRef.close(status);
  }

  logAsCurrentUser(serviceType: 'Userserviceurl' | 'Admserviceurl'): void {
    const payload: ILoginAsIm = {
      email: this.data.usersListPerPage[this.data.currentUserIndex].email,
      serviceType,
    };

    console.log('payload', payload);
    this._loginAsParamService
      .post(payload)
      .subscribe((res: ILogiAsResultVm) => {
        if (res.url) {
          window.open(res.url, '_blank'); // Open in a new tab
        }
      });
  }

  resetPassword(): void {
    const user = this.currentUser;
    this._confirmationService.confirm({
      key: 'platformUserAccountAction',
      header: 'Reset hasła',
      message: `Wygenerować nowe hasło dla konta ${user.email} i wysłać je na ten adres? Dotychczasowe hasło przestanie działać, a użytkownik po zalogowaniu zmieni nowe na własne.`,
      acceptLabel: 'Resetuj i wyślij',
      rejectLabel: 'Anuluj',
      defaultFocus: 'reject',
      accept: () => {
        this.passwordResetPending.add(user.guid);
        this._accountActions.add(this._platformUserPasswordService.post({ userGuid: user.guid }).subscribe({
          next: () => {
            this.passwordResetPending.delete(user.guid);
            this._messageService.add({ severity: 'success', summary: 'Hasło zresetowane',
              detail: `Nowe hasło zostało wysłane na adres ${user.email}.` });
          },
          error: error => {
            this.passwordResetPending.delete(user.guid);
            this._messageService.add({ severity: 'error', summary: 'Nie udało się zresetować hasła',
              detail: error?.error?.message });
          },
        }));
      },
    });
  }

  confirmEmail(): void {
    const user = this.currentUser;
    this._confirmationService.confirm({
      key: 'platformUserAccountAction',
      header: 'Potwierdzenie adresu e-mail',
      message: `Potwierdzić adres ${user.email} w imieniu użytkownika? Konto zostanie aktywowane, a użytkownik dostanie o tym wiadomość e-mail.`,
      acceptLabel: 'Potwierdź',
      rejectLabel: 'Anuluj',
      defaultFocus: 'reject',
      accept: () => {
        this.emailConfirmationPending.add(user.guid);
        this._accountActions.add(this._platformUserEmailConfirmationService.post({ userGuid: user.guid }).subscribe({
          next: () => {
            this.emailConfirmationPending.delete(user.guid);
            user.emailConfirmed = true;
            this._messageService.add({ severity: 'success', summary: 'Adres e-mail potwierdzony',
              detail: `Konto ${user.email} jest aktywne.` });
          },
          error: error => {
            this.emailConfirmationPending.delete(user.guid);
            this._messageService.add({ severity: 'error', summary: 'Nie udało się potwierdzić adresu e-mail',
              detail: error?.error?.message });
          },
        }));
      },
    });
  }

  resendActivationLink(): void {
    confirmAndResendActivationLink(this.currentUser, { confirmationKey: 'platformUserAccountAction', pending: this.activationLinkPending,
      subscriptions: this._accountActions, confirmationService: this._confirmationService,
      messageService: this._messageService, activationLinkService: this._platformUserActivationLinkService });
  }

  openAssignSubscription(mode: UsersSubscriptionDialogMode = 'assign'): void {
    this.assignSubscriptionRef = this._primengDialogService.open(
      UsersAddSubscriptionsComponent,
      {
        closable: false,
        showHeader: false,
        closeOnEscape: true,
        appendTo: 'body',
        width: '480px',
        breakpoints: {
          '480px': '100vw',
        },
        baseZIndex: 10001,
        data: {
          user: this.currentUser,
          mode,
        },
      }
    );

    this.assignSubscriptionRef.onClose.subscribe(
      (result?: IAssignedSubscriptionResult) => {
        if (result) {
          this.currentUser.subscriptionPlan = result.subscriptionPlan;
          this.currentUser.subscriptionPlanActiveTo =
            result.subscriptionPlanActiveTo;
        }
      }
    );
  }

  moveUp(): void {
    if (this.data.editMode) {
      this.data.editMode = false;
    }
    this.currentUserIndex =
      (this.currentUserIndex + 1) % this.data.usersListPerPage.length;
    this.currentUser = this.data.usersListPerPage[this.currentUserIndex];
    this.loadDevices();
    this.loadUserOsk();

    this.data.editMode = false;
  }

  moveDown(): void {
    if (this.data.editMode) {
      this.data.editMode = false;
    }
    this.currentUserIndex =
      (this.currentUserIndex - 1 + this.data.usersListPerPage.length) %
      this.data.usersListPerPage.length;
    this.currentUser = this.data.usersListPerPage[this.currentUserIndex];
    this.loadDevices();
    this.loadUserOsk();
    this.data.editMode = false;
  }


  private setReadOnlyFields(): ReadOnlyData[] {
    return [
      {
        name: 'guid',
        label: 'ID',
        type: 'input-string',
      },
      {
        name: 'companyName',
        label: 'Nazwa firmy',
        type: 'input-string',
      },

      {
        name: 'displayName',
        label: 'Nazwa użytkownika',
        type: 'input-string',
      },
      {
        name: 'created',
        label: 'Data utworzenia',
        type: 'calendar',
      },
      {
        name: 'firstName',
        label: 'Imię',
        type: 'input-string',
      },
      {
        name: 'lastName',
        label: 'Nazwisko',
        type: 'input-string',
      },

      {
        name: 'phone',
        label: 'Telefon',
        type: 'input-string',
      },
      {
        name: 'nip',
        label: 'NIP',
        type: 'input-string',
      },
      {
        name: 'email',
        label: 'Adres e-mail',
        type: 'input-string',
        fieldWidth: 'w-full',
      },
    ];
  }

  //TODO - to be refactored

  handleTabSwitch($event: any) {
    this.selectedTabIndex = $event.index;
  }

  getSubscriptionState(activeTo?: Date): { label: string; color: string } {
    if (!activeTo) {
      return { label: 'Brak daty ważności', color: '#9CA3AF' };
    }

    const now = new Date();
    const activeToDate = new Date(activeTo);
    const diffInDays = Math.ceil(
      (activeToDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (activeToDate < now) {
      return { label: 'Wygasł', color: '#9CA3AF' };
    }

    if (diffInDays <= 3) {
      return { label: 'Aktywny', color: '#F97316' };
    }

    return { label: 'Aktywny', color: '#22C55E' };
  }

  showSuccess() {
    this._messageService.add({
      severity: 'success',
      detail: 'Pytanie zaktualizowane poprawnie',
    });
  }
}
